import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material";

import { Resource, ResourceUpdateRequest } from "@impulse/sdp-shared-models";

import { ApiGatewayService } from "../../../common/api-gateway/api-gateway.service";
import { AccessSelection } from "./models";

@Injectable()
export class PolicyWizardService {
	public readonly PARENT_ROUTE = "policies";

	private _resource: Resource | undefined = undefined;
	private _selectedAccessData: AccessSelection[] | undefined = undefined;
	private _isCreate = false;

	constructor(
		public dialog: MatDialog,
		private apiGatewayService: ApiGatewayService
	) { }

	public get resource(): Resource | undefined {
		return this._resource;
	}

	public set resource(resource: Resource | undefined) {
		this._resource = resource;
	}

	public get selectedAccessData() {
		return this._selectedAccessData;
	}

	public set selectedAccessData(selectedAccessData: AccessSelection[] | undefined) {
		this._selectedAccessData = selectedAccessData;
	}

	public get isCreate() {
		return this._isCreate;
	}

	public newPolicy() {
		this._resource = undefined;
		this._selectedAccessData = undefined;
		this._isCreate = true;
	}

	public editPolicy(resource: Resource) {
		this._resource = resource;
		this._selectedAccessData = undefined;
		this._isCreate = false;
	}

	public closeDialog() {
		this.dialog.closeAll();
	}

	public async getAllPolicies() {
		const resources = await this.apiGatewayService.getAllApplications();
		return this.filterResourcesByPolicies(resources, true);
	}

	public async getResourcesMissingPolicies() {
		const resources = await this.apiGatewayService.getAllApplications();
		return this.filterResourcesByPolicies(resources, false);
	}

	public filterResourcesByPolicies(resources: Resource[], hasPolicies: boolean) {
		return resources.filter((resource) => {
			if (resource.access.users.length > 0 || resource.access.samlAliases.length > 0) {
				return hasPolicies;
			} else {
				return !hasPolicies;
			}
		});
	}

	/**
	 * Doesn't actually delete anything, just removes all existing access from the resource
	 * @param resource
	 */
	public deletePolicy(resource: Resource) {
		const request: ResourceUpdateRequest = {
			access: {
				users: {
					rem: resource.access.users
				},
				samlAliases: {
					rem: resource.access.samlAliases
				}
			}
		};
		return this.apiGatewayService.updateApplication(request, resource.urlName);
	}

	public updatePolicy(oldAccess: Resource.Access, newAccess: Resource.Access, resourceUrlName: string) {
		// Find the intersection of users and aliases for the original and updated resource
		const intersectUsers = oldAccess.users.filter((user) => newAccess.users.indexOf(user) !== -1);
		const intersectAliases = oldAccess.samlAliases.filter((alias) => newAccess.samlAliases.indexOf(alias) !== -1);
		const request: ResourceUpdateRequest = {
			access: {
				users: {
					// add users present in the new that aren't in the intersection
					add: newAccess.users.filter((user) => intersectUsers.indexOf(user) === -1),
					// remove users present in the old that aren't in the intersection
					rem: oldAccess.users.filter((user) => intersectUsers.indexOf(user) === -1)
				},
				samlAliases: {
					// add aliases present in the new that aren't in the intersection
					add: newAccess.samlAliases.filter((alias) => intersectAliases.indexOf(alias) === -1),
					// remove aliases present in the old that aren't in the intersection
					rem: oldAccess.samlAliases.filter((alias) => intersectAliases.indexOf(alias) === -1)
				}
			}
		};
		return this.apiGatewayService.updateApplication(request, resourceUrlName);
	}

	public createPolicy(resource: Resource) {
		const request: ResourceUpdateRequest = {
			access: {
				users: {
					add: resource.access.users
				},
				samlAliases: {
					add: resource.access.samlAliases
				}
			}
		};
		return this.apiGatewayService.updateApplication(request, resource.urlName);
	}
}
